import React, {useCallback, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {
    BtnBold,
    BtnBulletList,
    BtnClearFormatting,
    BtnItalic,
    BtnLink,
    BtnNumberedList,
    BtnRedo,
    BtnStrikeThrough,
    BtnStyles,
    BtnUnderline,
    BtnUndo,
    createButton,
    createDropdown,
    Editor,
    EditorProvider,
    Separator,
    Toolbar,
} from 'react-simple-wysiwyg';
import {Box} from '@mui/material';
import {FORM_ACCESS_LEVELS} from '../../../../../../constants/formAccessLevels';
import FORM_AUDIENCE_TYPE from '../../../../../../constants/FormAudienceType';
import {useTheme} from '@mui/material/styles';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import ColorPicker from './ColorPicker';
import ToggleColorPickerButton from './BtnToogleColorPicker';

const schoolPlaceHolder = [
    ['School Name', 'InsertText', '@SchoolName '],
];

const ALL_PLACEHOLDERS = [
    {
        key: 'parent',
        label: 'Parent',
        token: '@Parent '
    },
    {
        key: 'parentSurname',
        label: 'Parent Surname',
        token: '@ParentSurname '
    },
    {
        key: 'studentForename',
        label: 'Student Forename',
        token: '@StudentForename '
    },
    {
        key: 'studentFullName',
        label: 'Student Full Name',
        token: '@StudentFullName '
    },
    {
        key: 'hisHer',
        label: 'His/Her',
        token: '@HisHer '
    },
    {
        key: 'heShe',
        label: 'He/She',
        token: '@HeShe '
    },
    {
        key: 'himHer',
        label: 'Him/Her',
        token: '@HimHer '
    },
    {
        key: 'title',
        label: 'Title',
        token: '@Title '
    },
    {
        key: 'employee',
        label: 'Employee',
        token: '@Employee '
    },
];

const getAudienceType = (audience, recipientType) => {
    if (!audience) return [];

    const studentPlaceholders = ['studentForename', 'studentFullName', 'hisHer', 'heShe', 'himHer'];

    switch (audience) {
        case FORM_AUDIENCE_TYPE.students:
            return studentPlaceholders;

        case FORM_AUDIENCE_TYPE.parentsForChildCompletion:
            return [
                ...studentPlaceholders,
                'parent',
                'parentSurname',
                'title',
            ];

        case FORM_AUDIENCE_TYPE.employees:
            return ['title', 'employee'];

        case FORM_AUDIENCE_TYPE.previousSchool:
            return studentPlaceholders;

        case FORM_AUDIENCE_TYPE.externalUsers: {
            if (recipientType === 'student') {
                return studentPlaceholders;
            }

            if (recipientType === 'employee') {
                return ['employee'];
            }
            return [];
        }

        default:
            return null;
    }
};

const BtnAlignCenter = createButton(
    'Align center',
    <FormatAlignCenterIcon fontSize="small" sx={{marginTop: '3px'}}/>,
    'justifyCenter'
);
const BtnAlignLeft = createButton(
    'Align left',
    <FormatAlignLeftIcon fontSize="small" sx={{marginTop: '3px'}}/>,
    'justifyLeft'
);
const BtnAlignRight = createButton(
    'Align right',
    <FormatAlignRightIcon fontSize="small" sx={{marginTop: '3px'}}/>,
    'justifyRight'
);

const RichEditor = ({
                        index,
                        value,
                        currentDescription,
                        setCurrentDescription,
                        isFormLive,
                        accessLevel,
                        style,
                        isInModal = false,
                        additionalPlaceholders = [],
                        audience,
                        recipientType,
                    }) => {
    const [showPicker, setShowPicker] = useState(false);
    const buttonRef = useRef(null);
    const [textColor, setTextColor] = useState('#000000');
    const theme = useTheme();

    const audienceType = getAudienceType(audience, recipientType);

    const audiencePlaceholders = ALL_PLACEHOLDERS
        .filter(p => !Array.isArray(audienceType) || audienceType.includes(p.key))
        .map(p => [p.label, 'InsertText', p.token]);

    const privateOptions = [
        ...schoolPlaceHolder,
        ...audiencePlaceholders,
    ];

    const publicOptions = [
        ...schoolPlaceHolder,
        ...audiencePlaceholders,
        ...additionalPlaceholders,
    ];

    const usePrivateSet =
        accessLevel === FORM_ACCESS_LEVELS.private ||
        accessLevel === FORM_ACCESS_LEVELS.publicWithStudentLink;

    const placeholderOptions = usePrivateSet ? privateOptions : publicOptions;
    const PlaceholderDropdown = createDropdown('Placeholders', placeholderOptions);

    const handlePlaceholderSelect = (placeholder) => {
        setCurrentDescription(currentDescription + placeholder);
    };

    const applyColorCommand = useCallback((color) => {
        setTextColor(color);
        document.execCommand('styleWithCSS', false, true);
        document.execCommand('foreColor', false, color);
    }, []);

    return (
        <Box sx={{pt: 1}}>
            <EditorProvider>
                <Editor
                    disabled={isFormLive}
                    value={currentDescription}
                    onChange={e => setCurrentDescription(e.target.value)}
                    containerProps={{
                        style: {
                            ...theme.typography.body1,
                            color: theme.palette.text.primary,
                            ...style.editor,
                        },
                    }}
                >
                    <Toolbar style={{...style.toolbar}}>
                        <BtnUndo/>
                        <BtnRedo/>
                        <Separator/>
                        <BtnBold/>
                        <BtnItalic/>
                        <BtnUnderline/>
                        <BtnStrikeThrough/>
                        <ToggleColorPickerButton
                            onClick={() => setShowPicker(!showPicker)}
                            ref={buttonRef}
                            color={textColor}
                        />
                        <Separator/>
                        <BtnNumberedList/>
                        <BtnBulletList/>
                        <BtnAlignLeft/>
                        <BtnAlignCenter/>
                        <BtnAlignRight/>
                        <Separator/>
                        <BtnLink/>
                        <BtnClearFormatting/>
                        <Separator/>
                        <BtnStyles/>
                        <Separator/>
                        {showPicker && (
                            <ColorPicker
                                applyColorCommand={applyColorCommand}
                                onClose={() => setShowPicker(false)}
                                anchorEl={buttonRef.current}
                                isInModal={isInModal}
                            />
                        )}
                        <PlaceholderDropdown onChange={value => handlePlaceholderSelect(value)}/>
                    </Toolbar>
                </Editor>
            </EditorProvider>
        </Box>
    );
};

RichEditor.defaultProps = {
    style: {
        editor: {},
        toolbar: {},
    },
    isInModal: false,
    additionalPlaceholders: [],
};

RichEditor.propTypes = {
    index: PropTypes.number.isRequired,
    value: PropTypes.string.isRequired,
    currentDescription: PropTypes.string.isRequired,
    setCurrentDescription: PropTypes.func.isRequired,
    isFormLive: PropTypes.bool.isRequired,
    accessLevel: PropTypes.number.isRequired,
    style: PropTypes.object,
    isInModal: PropTypes.bool,
    additionalPlaceholders: PropTypes.array,
    audience: PropTypes.number,
    recipientType: PropTypes.string
};

export default RichEditor;
